<template>
  <div class="class-1">
    <!-- Header -->
    <AppHeader />

    <!-- Permissions Section -->
    <div class="container mt-4">
      <div class="row">
        <div class="col-12">
          <h2>Permissions</h2>

          <div v-if="loading" class="text-center">Loading permissions...</div>
          <div v-else>
            <!-- Categories Grid -->
            <div class="categories-grid mb-4">
              <div 
                v-for="(permissions, category) in groupedPermissions" 
                :key="category" 
                class="category-card"
                :class="{ 'active': isCategoryOpen(category) }"
                @click="toggleCategory(category)"
              >
                 <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center">
                    <h5 class="mb-0">{{ category }}</h5>
                    <span :class="{ 'text-success': isCategoryOpen(category) }">
                      {{ isCategoryOpen(category) ? '▲' : '▼' }}
                    </span>
                  </div>
                  <div class="form-check mt-2">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :checked="areAllPermissionsSelected(category)"
                      @click.stop
                      @change="toggleAllPermissions(category, $event)"
                    />
                    <label class="form-check-label">Select All</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Permissions Display Area -->
            <div 
              v-if="openCategories.length > 0" 
              class="permissions-display"
              ref="permissionsDisplay"
            >
              <div v-for="category in openCategories" :key="category" class="mb-4">
                <h4 class="mb-3">{{ category }} Permissions</h4>
                <div class="permissions-list">
                  <div 
                    v-for="permission in groupedPermissions[category]" 
                    :key="permission.id" 
                    class="permission-item"
                  >
                    <div class="form-check">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        v-model="selectedPermissions"
                        :value="permission.id"
                        :id="'perm-' + permission.id"
                      />
                      <label class="form-check-label" :for="'perm-' + permission.id">
                        {{ permission.name }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Save Changes Button -->
          <div class="text-right mt-4 mb-4">
            <button class="btn btn-primary" @click="saveChanges">Save Changes</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      groupedPermissions: {},
      selectedPermissions: [], 
      openCategories: [],
      loading: true,
      currentDate: '',
      currentTime: '',
      userId: null, 
      userPermissions: [], 
      branches: [],
      selectedBranch: '', 
    };
  },
  mounted() {
    this.updateDateTime();
    this.startDateTimeUpdate();
    this.fetchPermissions();
    this.userId = this.$route.params.id;

    const storedPermissions = localStorage.getItem('userPermissions');
    if (storedPermissions) {
      this.userPermissions = JSON.parse(storedPermissions);
    }
  },
  methods: {
    areAllPermissionsSelected(category) {
      const categoryPermissions = this.groupedPermissions[category] || [];
      return categoryPermissions.length > 0 && 
             categoryPermissions.every(permission => 
               this.selectedPermissions.includes(permission.id)
             );
    },

    toggleAllPermissions(category, event) {
      const categoryPermissions = this.groupedPermissions[category] || [];
      const permissionIds = categoryPermissions.map(p => p.id);
      
      if (event.target.checked) {
        permissionIds.forEach(id => {
          if (!this.selectedPermissions.includes(id)) {
            this.selectedPermissions.push(id);
          }
        });
      } else {
        this.selectedPermissions = this.selectedPermissions.filter(
          id => !permissionIds.includes(id)
        );
      }
    },
    
    async fetchPermissions() {
      try {
        this.$root.isLoading = true; 
        this.loading = true;
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${this.$BASE_API_URL}/api/selection/permissions_grouped_by_cateogry`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch permissions');
        }

        const data = await response.json();
        this.groupedPermissions = data.data;

        const permissionNameToIdMap = {};
        for (const category in this.groupedPermissions) {
          this.groupedPermissions[category].forEach((permission) => {
            permissionNameToIdMap[permission.name] = permission.id;
          });
        }

        this.selectedPermissions = this.userPermissions
          .map((permissionName) => permissionNameToIdMap[permissionName])
          .filter((id) => id !== undefined);
      } catch (error) {
        console.error('Error fetching permissions:', error);
        this.$toast.error('Failed to load permissions. Please try again.');
      } finally {
        this.$root.isLoading = false;
        this.loading = false;
      }
    },
    
    toggleCategory(category) {
      if (this.isCategoryOpen(category)) {
        this.openCategories = [];
      } else {
        this.openCategories = [category];
        this.$nextTick(() => {
          this.scrollToPermissions();
        });
      }
    },
    
    scrollToPermissions() {
      const element = this.$refs.permissionsDisplay;
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    
    isCategoryOpen(category) {
      return this.openCategories.includes(category);
    },
    
    updateDateTime() {
      const now = new Date();
      this.currentDate = now.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      this.currentTime = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    
    startDateTimeUpdate() {
      setInterval(() => {
        this.updateDateTime();
      }, 1000);
    },
    
    async saveChanges() {
      try {
        this.$root.isLoading = true; 
        this.loading = true;
        const token = localStorage.getItem('authToken');
        if (!token) {
          this.$toast.error('No token found. Please log in again.');
          return;
        }
        const permissionsPayload = {
          permissions: this.selectedPermissions,
        };

        const permissionsResponse = await fetch(`${this.$BASE_API_URL}/api/users/${this.userId}/permissions`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': '*/*',
            'keep-alive': 'keep-alive',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(permissionsPayload),
        });

        if (!permissionsResponse.ok) {
          throw new Error('Failed to save permissions');
        }

        if (this.selectedBranch) {
          const branchPayload = {
            appId: 1,
            branchesId: [this.selectedBranch],
          };

          const branchResponse = await fetch(`${this.$BASE_API_URL}/api/users/${this.userId}/branches`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(branchPayload),
          });

          if (!branchResponse.ok) {
            throw new Error('Failed to save branch');
          }
        }

        this.$toast.success('Changes saved successfully!');
        this.$router.push('/users');
      } catch (error) {
        console.error('Error saving changes:', error);
        this.$toast.error('Failed to save changes. Please try again.');
      }
      finally {
        this.$root.isLoading = false;
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.category-card.active span {
  color: #28a745; 
  transition: color 0.3s ease;
}

.category-card span {
  transition: transform 0.3s ease, color 0.3s ease;
}

.category-card.active span {
  transform: rotate(180deg);
}
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.category-card {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.category-card.active {
  border-color: #adb5bd;
}

.permissions-display {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-top: 1rem;
}

.permissions-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.5rem;
}

.permission-item {
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s ease;
}


.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}
</style>